import { 
  Edit, 
  Trash2, 
  Ruler, 
  User, 
  Calendar,
  FileText,
  Activity,
  Eye
} from 'lucide-react'
import { LoadingSpinner } from '../common'

function MeasurementList({ 
  measurements, 
  isLoading, 
  onEdit, 
  onDelete, 
  onToggleStatus,
  onView,
  userRole
}) {
  if (isLoading) {
    return (
      <div className="card">
        <div className="card-content">
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    )
  }

  if (!measurements || measurements.length === 0) {
    return (
      <div className="card">
        <div className="card-content">
          <div className="text-center py-12">
            <Ruler className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-base font-medium text-secondary-900">No measurements found</h3>
            <p className="mt-1 text-base text-secondary-500">
              Get started by creating a new measurement record.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-content p-0">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Customer</th>
                <th className="table-header-cell">Measurement Type</th>
                <th className="table-header-cell">Values</th>
                <th className="table-header-cell">Notes</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Created</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {measurements.map((measurement) => (
                <tr key={measurement.id} className="table-row">
                  <td className="table-cell">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-base font-medium text-secondary-900">
                          {measurement.customer?.firstName} {measurement.customer?.lastName}
                        </div>
                        <div className="text-base text-secondary-500">
                          ID: {measurement.customerId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center">
                      <Ruler className="h-4 w-4 mr-2 text-primary-600" />
                      <span className="text-base font-medium text-secondary-900">
                        {measurement.measurementType || 'Standard'}
                      </span>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="space-y-1">
                      {measurement.length && (
                        <div className="text-base text-secondary-600">
                          <span className="font-medium">Length:</span> {measurement.length}"
                        </div>
                      )}
                      {measurement.bust && (
                        <div className="text-base text-secondary-600">
                          <span className="font-medium">Bust:</span> {measurement.bust}"
                        </div>
                      )}
                      {measurement.waist && (
                        <div className="text-base text-secondary-600">
                          <span className="font-medium">Waist:</span> {measurement.waist}"
                        </div>
                      )}
                      {measurement.hip && (
                        <div className="text-base text-secondary-600">
                          <span className="font-medium">Hip:</span> {measurement.hip}"
                        </div>
                      )}
                      {measurement.shoulder && (
                        <div className="text-base text-secondary-600">
                          <span className="font-medium">Shoulder:</span> {measurement.shoulder}"
                        </div>
                      )}
                      {measurement.chest && (
                        <div className="text-base text-secondary-600">
                          <span className="font-medium">Chest:</span> {measurement.chest}"
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="text-base text-secondary-600 max-w-xs">
                      {measurement.notes ? (
                        <div className="flex items-start">
                          <FileText className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="truncate">
                            {measurement.notes}
                          </span>
                        </div>
                      ) : (
                        <span className="text-secondary-400">No notes</span>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      measurement.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {measurement.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center text-base text-secondary-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      {measurement.createdAt ? new Date(measurement.createdAt).toLocaleDateString() : 'N/A'}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onView(measurement)}
                        className="text-secondary-600 hover:text-secondary-900"
                        title="View Measurement"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onEdit(measurement)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit Measurement"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onToggleStatus(measurement)}
                        className={measurement.isActive ? 'text-danger-600 hover:text-danger-900' : 'text-success-600 hover:text-success-900'}
                        title={measurement.isActive ? 'Deactivate Measurement' : 'Activate Measurement'}
                      >
                        {measurement.isActive ? <Activity className="h-4 w-4" /> : <Ruler className="h-4 w-4" />}
                      </button>
                      {/* Only Super Admin can delete measurements */}
                      {userRole === 1 && (
                        <button
                          onClick={() => onDelete(measurement)}
                          className="text-danger-600 hover:text-danger-900"
                          title="Delete Measurement"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default MeasurementList
